"use client";

import {
  isValidElement,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
  type FC,
  type Ref,
} from "react";
import { motion, AnimatePresence } from "motion/react";
import { mergeClasses } from "@huub-web/utils";

import { Flex } from "../Flex";
import { Text } from "../Text";
import { Card, type CardProps } from "../Card";
import { IconButton } from "../IconButton";
import { transitionDurations } from "../../theme/transitionDurations";
import { useHover } from "../../hooks/useHover";
import { useUuid } from "../../hooks/useUuid";
import { Indicator, type IndicatorProps } from "../Indicator";
import { mergeRefs } from "../../utils/mergeRefs";
import type { ChildrenProp, IconProp } from "../../props";
import type { IconButtonProps } from "../IconButton";

import { focusVisibleOffset } from "../../theme/css/focusVisible.css";

import {
  iconStyle,
  contentStyle,
  handleStyle,
  titleStyle,
  padderStyle,
} from "./ExpansionPanel.css";

export type ExpansionPanelProps = CardProps &
  IconProp &
  ChildrenProp & {
    title: ReactNode;
    subtitle?: string;
    indicatorVariant?: IndicatorProps["variant"];
    indicatorType?: IndicatorProps["type"];
    open?: boolean;
    defaultOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
    trailing?: ReactNode;
    customContent?: ReactNode;
    /**
     * When true, the content is always rendered in the DOM even when collapsed.
     * Useful for preserving form state or avoiding re-mounting expensive components.
     */
    forceMount?: boolean;
    /**
     * When true, disables full-card click to open.
     * Use when providing custom trailing controls.
     */
    customControlled?: boolean;
    /**
     * Size of the default chevron IconButton.
     * @default "inline"
     */
    iconButtonSize?: IconButtonProps["size"];
    /**
     * Numeric index used by auto-scroll to identify this panel in the DOM.
     * Renders as a `data-panel-index` attribute on the outermost wrapper.
     */
    panelIndex?: number;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLDivElement>;
  };

/**
 * ExpansionPanel component is used to show and hide content.
 * @todo Extend BasicCard
 */
export const ExpansionPanel: FC<ExpansionPanelProps> = ({
  title,
  subtitle,
  variant = "primary",
  icon,
  indicatorVariant = "accent",
  indicatorType = "square",
  open: controlledOpen,
  defaultOpen = false,
  border = "default",
  trailing,
  customContent,
  onOpenChange,
  forceMount = false,
  customControlled = false,
  iconButtonSize = "inline",
  panelIndex,
  children,
  ref,
  ...props
}) => {
  // Internal state for uncontrolled mode
  const [internalOpen, setInternalOpen] = useState(defaultOpen);

  // Determine if component is controlled
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const { handleEnter, handleLeave, hasHover } = useHover();

  const cardRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isAriaHidden, setIsAriaHidden] = useState(!open);
  const inertProps = isAriaHidden
    ? ({ inert: "" } as unknown as { inert: string })
    : {};

  const contentID = useUuid();
  const buttonID = useUuid();

  const handleToggle = () => {
    const newOpen = !open;

    if (isControlled) {
      onOpenChange?.(newOpen);
    } else {
      setInternalOpen(newOpen);
      onOpenChange?.(newOpen);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!open && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      handleToggle();
    }
  };

  /**
   * Avoid hiding a focused descendant when collapsing.
   */
  useEffect(() => {
    if (!forceMount) {
      return;
    }

    if (open) {
      setIsAriaHidden(false);
      return;
    }

    const contentElement = contentRef.current;
    const activeElement = document.activeElement;
    const isFocusInside =
      !!contentElement &&
      !!activeElement &&
      contentElement.contains(activeElement);

    if (isFocusInside) {
      cardRef.current?.focus();
    }

    setIsAriaHidden(true);
  }, [forceMount, open]);

  return (
    <Card
      id={buttonID}
      data-panel-index={panelIndex}
      tabIndex={open || customControlled ? -1 : 0}
      ref={mergeRefs(ref, cardRef)}
      border={border}
      aria-expanded={open}
      variant={variant}
      onClick={open || customControlled ? undefined : handleToggle}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onKeyDown={handleKeyDown}
      {...props}
    >
      <Flex direction="column">
        <Flex
          className={mergeClasses(handleStyle(), focusVisibleOffset)}
          align="center"
          aria-expanded={open}
          aria-controls={contentID}
          gap="s"
          data-panel-header=""
        >
          {icon && (
            <Indicator
              icon={icon}
              variant={indicatorVariant}
              type={indicatorType}
              size="inline"
            />
          )}
          <Flex grow direction="column">
            {isValidElement(title) ? (
              title
            ) : (
              <Text className={titleStyle()}>{title}</Text>
            )}
            {subtitle && (
              <Text ellipsis variant="bodyM" color="currentColor50">
                {subtitle}
              </Text>
            )}
          </Flex>
          {trailing ? (
            trailing
          ) : (
            <IconButton
              as={open ? "button" : "div"}
              className={iconStyle({ open })}
              size={iconButtonSize}
              icon="chevronDown"
              border="default"
              variant="opaque"
              active={hasHover && !open}
              onClick={open ? handleToggle : undefined}
              rounded
              aria-label={`${open ? "Collapse" : "Expand"} ${title} section`}
            />
          )}
        </Flex>

        {forceMount ? (
          <motion.div
            id={contentID}
            role="region"
            aria-labelledby={buttonID}
            aria-hidden={isAriaHidden}
            {...inertProps}
            className={contentStyle()}
            ref={contentRef}
            initial={false}
            animate={open ? "open" : "collapsed"}
            variants={{
              open: { opacity: 1, height: "auto" },
              collapsed: { opacity: 0, height: 0 },
            }}
            transition={{
              duration: transitionDurations.m,
              ease: "easeInOut",
            }}
          >
            {customContent ? (
              customContent
            ) : (
              <div className={padderStyle({})}>{children}</div>
            )}
          </motion.div>
        ) : (
          <AnimatePresence initial={false}>
            {open && (
              <motion.div
                id={contentID}
                role="region"
                aria-labelledby={buttonID}
                className={contentStyle()}
                key="content"
                initial="collapsed"
                animate="open"
                exit="collapsed"
                variants={{
                  open: { opacity: 1, height: "auto" },
                  collapsed: { opacity: 0, height: 0 },
                }}
                transition={{
                  duration: transitionDurations.m,
                  ease: "easeInOut",
                }}
              >
                {customContent ? (
                  customContent
                ) : (
                  <div className={padderStyle({})}>{children}</div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </Flex>
    </Card>
  );
};
